import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Undo2, Trash2, Save, Eraser, Pen } from '../../icons/wp-icons';

const FARBEN = [
  { name: 'Schwarz', value: '#000000' },
  { name: 'Rot', value: '#EF4444' },
  { name: 'Blau', value: '#3B82F6' },
  { name: 'Grün', value: '#22C55E' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Lila', value: '#A855F7' },
  { name: 'Gelb', value: '#EAB308' },
  { name: 'Weiß', value: '#FFFFFF' },
];

const STIFT_GROESSEN = [
  { name: 'Fein', value: 2 },
  { name: 'Mittel', value: 5 },
  { name: 'Dick', value: 10 },
  { name: 'Sehr dick', value: 20 },
];

function SkizzenPad({ onSave, onClose, saving = false, initialImageUrl = null }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentColor, setCurrentColor] = useState('#000000');
  const [currentSize, setCurrentSize] = useState(5);
  const [isEraser, setIsEraser] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const lastPoint = useRef(null);

  // Body Scroll Lock
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Canvas initialisieren
  useEffect(() => {
    const canvas = canvasRef.current;
    const resizeCanvas = () => {
      const container = canvas.parentElement;
      const rect = container.getBoundingClientRect();
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      tempCtx.drawImage(canvas, 0, 0);

      canvas.width = rect.width;
      canvas.height = rect.height;

      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (tempCanvas.width > 0 && tempCanvas.height > 0) {
        ctx.drawImage(tempCanvas, 0, 0);
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  // Bild laden wenn initialImageUrl vorhanden
  useEffect(() => {
    if (!initialImageUrl || imageLoaded) return;
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Bild proportional einpassen
      const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      const x = (canvas.width - w) / 2;
      const y = (canvas.height - h) / 2;
      ctx.drawImage(img, x, y, w, h);

      setHasContent(true);
      setImageLoaded(true);
    };
    img.src = initialImageUrl;
  }, [initialImageUrl, imageLoaded]);

  const saveSnapshot = useCallback(() => {
    const canvas = canvasRef.current;
    const snapshot = canvas.toDataURL();
    setUndoStack(prev => {
      const next = [...prev, snapshot];
      if (next.length > 30) next.shift();
      return next;
    });
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      pressure: e.pressure || 0.5,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    saveSnapshot();
    const point = getPoint(e);
    lastPoint.current = point;
    setIsDrawing(true);
    setHasContent(true);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = isEraser ? '#ffffff' : currentColor;
    const size = isEraser ? currentSize * 2 : currentSize;
    ctx.beginPath();
    ctx.arc(point.x, point.y, size / 2, 0, Math.PI * 2);
    ctx.fill();
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const point = getPoint(e);
    const prev = lastPoint.current;

    if (!prev) {
      lastPoint.current = point;
      return;
    }

    ctx.strokeStyle = isEraser ? '#ffffff' : currentColor;
    const pressure = Math.max(0.1, point.pressure);
    const size = isEraser ? currentSize * 2 : currentSize;
    ctx.lineWidth = size * (0.5 + pressure * 0.5);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();

    lastPoint.current = point;
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      lastPoint.current = null;
    }
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const lastSnapshot = undoStack[undoStack.length - 1];

    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = lastSnapshot;

    setUndoStack(prev => prev.slice(0, -1));
    if (undoStack.length <= 1) {
      setHasContent(false);
    }
  };

  const clearCanvas = () => {
    if (!hasContent) return;
    saveSnapshot();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasContent(false);
  };

  const handleSave = () => {
    if (!hasContent) return;
    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `Skizze_${Date.now()}.png`, { type: 'image/png' });
        onSave(file);
      }
    }, 'image/png');
  };

  const selectColor = (color) => {
    setCurrentColor(color);
    setIsEraser(false);
  };

  return (
    <div
      className="fixed inset-0 bg-white z-[9999] flex flex-col"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* Toolbar */}
      <div className="border-b border-gray-200 bg-gray-50 flex-shrink-0">
        {/* Zeile 1: Werkzeuge + Stiftgrößen */}
        <div className="flex items-center px-3 py-2 gap-2">
          {/* Close */}
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition flex-shrink-0"
            title="Schließen"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>

          <div className="w-px h-6 bg-gray-300 flex-shrink-0" />

          {/* Pen / Eraser */}
          <button
            onClick={() => setIsEraser(false)}
            className={`p-2 rounded-lg transition flex-shrink-0 ${!isEraser ? 'bg-primary text-white' : 'hover:bg-gray-200 text-gray-600'}`}
            title="Stift"
          >
            <Pen className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIsEraser(true)}
            className={`p-2 rounded-lg transition flex-shrink-0 ${isEraser ? 'bg-primary text-white' : 'hover:bg-gray-200 text-gray-600'}`}
            title="Radierer"
          >
            <Eraser className="w-5 h-5" />
          </button>

          <div className="w-px h-6 bg-gray-300 flex-shrink-0" />

          {/* Stiftgrößen - direkte Buttons */}
          {STIFT_GROESSEN.map((s) => (
            <button
              key={s.value}
              onClick={() => setCurrentSize(s.value)}
              className={`flex items-center justify-center w-9 h-9 rounded-lg transition flex-shrink-0 ${
                currentSize === s.value
                  ? 'bg-gray-800 ring-2 ring-primary'
                  : 'bg-white border border-gray-300 hover:bg-gray-100'
              }`}
              title={s.name}
            >
              <span
                className={`rounded-full ${currentSize === s.value ? 'bg-white' : 'bg-gray-700'}`}
                style={{ width: Math.min(s.value + 2, 22), height: Math.min(s.value + 2, 22) }}
              />
            </button>
          ))}

          <div className="flex-1" />

          {/* Undo + Clear */}
          <button
            onClick={handleUndo}
            disabled={undoStack.length === 0}
            className="p-2 hover:bg-gray-200 rounded-lg transition disabled:opacity-30 flex-shrink-0"
            title="Rückgängig"
          >
            <Undo2 className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={clearCanvas}
            disabled={!hasContent}
            className="p-2 hover:bg-red-100 rounded-lg transition disabled:opacity-30 flex-shrink-0"
            title="Alles löschen"
          >
            <Trash2 className="w-5 h-5 text-red-500" />
          </button>
        </div>

        {/* Zeile 2: Farben */}
        <div className="flex items-center justify-center gap-2 px-3 pb-2">
          {FARBEN.map((farbe) => (
            <button
              key={farbe.value}
              onClick={() => selectColor(farbe.value)}
              className={`w-8 h-8 rounded-full flex-shrink-0 transition border-2 ${
                currentColor === farbe.value && !isEraser
                  ? 'border-primary scale-110 shadow-md'
                  : farbe.value === '#FFFFFF'
                    ? 'border-gray-400 hover:scale-105'
                    : 'border-gray-300 hover:scale-105'
              }`}
              style={{ backgroundColor: farbe.value }}
              title={farbe.name}
            />
          ))}
        </div>
      </div>

      {/* Canvas Area */}
      <div className="flex-1 relative overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full touch-none"
          style={{ cursor: isEraser ? 'cell' : 'crosshair' }}
          onPointerDown={startDrawing}
          onPointerMove={draw}
          onPointerUp={stopDrawing}
          onPointerLeave={stopDrawing}
          onPointerCancel={stopDrawing}
        />
      </div>

      {/* Footer */}
      <div
        className="flex items-center justify-center gap-4 px-4 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0"
      >
        <button
          onClick={onClose}
          className="flex-1 max-w-[200px] flex items-center justify-center gap-2 px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-100 transition font-medium"
        >
          Skizze verwerfen
        </button>
        <button
          onClick={handleSave}
          disabled={!hasContent || saving}
          className="flex-1 max-w-[200px] flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Speichern...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Skizze speichern
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default SkizzenPad;
